import { getEntraApiAccessToken } from '@/services/auth-utils.js';

const API_BASE = process.env.VUE_APP_ENTRA_API_BASE_URL || 'http://localhost:3000';

function normaliseError(error) {
  const responseError = error && error.response && error.response.data && error.response.data.error;
  return {
    code: responseError ? responseError.code : (error && error.code ? error.code : 'UNKNOWN_API_ERROR'),
    message: responseError ? responseError.message : (error && error.message || 'An unexpected error occurred.')
  };
}

export async function listChatSessions() {
  try {
    const token = await getEntraApiAccessToken();
    const response = await fetch(`${API_BASE}/api/v1/chat/sessions`, {
      headers: { Authorization: 'Bearer ' + token }
    });
    if (!response.ok) throw new Error('Failed to load sessions');
    const data = await response.json();
    return data.items || [];
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getChatSessionMessages(sessionId) {
  try {
    const token = await getEntraApiAccessToken();
    const response = await fetch(`${API_BASE}/api/v1/chat/sessions/${sessionId}/messages`, {
      headers: { Authorization: 'Bearer ' + token }
    });
    if (!response.ok) throw new Error('Failed to load session messages');
    return await response.json();
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function deleteChatSession(sessionId) {
  try {
    const token = await getEntraApiAccessToken();
    const response = await fetch(`${API_BASE}/api/v1/chat/sessions/${sessionId}`, {
      method: 'DELETE',
      headers: { Authorization: 'Bearer ' + token }
    });
    if (!response.ok) throw new Error('Failed to delete session');
    return await response.json();
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function deleteAllChatSessions() {
  try {
    const token = await getEntraApiAccessToken();
    const response = await fetch(`${API_BASE}/api/v1/chat/sessions`, {
      method: 'DELETE',
      headers: { Authorization: 'Bearer ' + token }
    });
    if (!response.ok) throw new Error('Failed to clear sessions');
    return await response.json();
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function streamChatMessage(message, sessionId, callbacks) {
  const token = await getEntraApiAccessToken();
  const response = await fetch(`${API_BASE}/api/v1/chat`, {
    method: 'POST',
    headers: {
      Authorization: 'Bearer ' + token,
      'Content-Type': 'application/json',
      Accept: 'text/event-stream'
    },
    body: JSON.stringify({ sessionId: sessionId || undefined, message })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error?.message || `Chat request failed: ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    let currentEvent = null;

    for (const line of lines) {
      if (line.startsWith('event: ')) {
        currentEvent = line.slice(7).trim();
      } else if (line.startsWith('data: ')) {
        const data = line.slice(6);
        if (!currentEvent) continue;
        try {
          const parsed = JSON.parse(data);
          if (callbacks.onEvent) callbacks.onEvent(currentEvent, parsed);
          if (currentEvent === 'session' && callbacks.onSession) callbacks.onSession(parsed);
          if (currentEvent === 'token' && callbacks.onToken) callbacks.onToken(parsed.text);
          if (currentEvent === 'tool_call' && callbacks.onToolCall) callbacks.onToolCall(parsed);
          if (currentEvent === 'tool_result' && callbacks.onToolResult) callbacks.onToolResult(parsed);
          if (currentEvent === 'done' && callbacks.onDone) callbacks.onDone(parsed);
          if (currentEvent === 'error' && callbacks.onError) callbacks.onError(parsed);
        } catch (e) {
          // skip malformed JSON
        }
        currentEvent = null;
      } else if (line.trim() === '') {
        currentEvent = null;
      }
    }
  }

  if (buffer.trim()) {
    const lines = buffer.split('\n');
    let currentEvent = null;
    for (const line of lines) {
      if (line.startsWith('event: ')) {
        currentEvent = line.slice(7).trim();
      } else if (line.startsWith('data: ')) {
        const data = line.slice(6);
        if (!currentEvent) continue;
        try {
          const parsed = JSON.parse(data);
          if (callbacks.onEvent) callbacks.onEvent(currentEvent, parsed);
          if (currentEvent === 'done' && callbacks.onDone) callbacks.onDone(parsed);
        } catch (e) {
          // skip
        }
      }
    }
  }
}
